import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Check, Clock, ChevronRight, ChevronDown, CalendarClock,
} from 'lucide-react'
import { Progress } from '@meno/ui/components/ui/progress'
import { cn, formatRelativeSyncTime } from '@meno/ui/lib/utils'
import type { Task } from '@meno/ui/lib/api'
import { isUploadTask, type AnyTask } from '@/contexts/TaskCenterContext'
import { contentRoute } from '@/lib/contentRoutes'
import { activityMetaFor, activityTitle } from '@/lib/activityTypes'

// Shared task-feed rendering used by both the full Tasks page and the
// TopBar Task Center modal, so the two never drift in style or behaviour.
// Icon / category / headline-template metadata for each Task.type comes from
// the shared activityTypes registry (also used by ScreenActivityLog) —
// one place defines what every activity type looks like everywhere.

const PUBLISH_TYPES = new Set<Task['type']>([
  'publish_playlist', 'publish_layout', 'publish_media', 'publish_menu', 'publish_audio',
  'publish_audio_playlist', 'publish_stop_screen', 'publish_transition', 'content_expire',
])
const isPublish = (task: AnyTask) => !isUploadTask(task) && PUBLISH_TYPES.has(task.type)

// Grouped publishes whose contents (playlist tracks / layout zones / audio
// tracks) are worth revealing in the expander via the frozen snapshot.
const GROUPED_TYPES = new Set<Task['type']>([
  'publish_playlist', 'publish_layout', 'publish_audio_playlist',
])

const appliedCount = (task: Task) => task.screens.filter(s => s.status === 'applied').length

const screenName = (sc: Task['screens'][number]) => sc.screen_name || `#${sc.screen_id}`

// Compact "who is this for" summary shown on the row itself (the full
// per-screen delivery list lives behind the expander): first two screen
// names + how many more.
const screenSummary = (task: Task): string | null => {
  if (task.screens.length === 0) return null
  const names = task.screens.map(screenName)
  const shown = names.slice(0, 2).join(', ')
  return names.length > 2 ? `${shown} +${names.length - 2}` : shown
}

// Deep-link for the published content, from the {kind, id} the backend stamps
// into task.meta on publish (shared route map with the screen activity log).
const taskContentRoute = (task: Task): string | null =>
  contentRoute(task.meta?.kind, task.meta?.id)

// Frozen "what was actually sent" record the backend stamps into task.meta on
// publish (item count + first few names + a version stamp). Survives the live
// content being edited or deleted, so an old activity row can still say what
// went out even when the link now 404s.
type TaskSnapshot = {
  item_count?: number
  item_names?: string[]
  updated_at?: string
  template?: string
  zone_count?: number
  menu_type?: string
  product_count?: number
}
const taskSnapshot = (task: Task): TaskSnapshot | null => {
  const s = (task.meta as { snapshot?: unknown } | null | undefined)?.snapshot
  return s && typeof s === 'object' ? (s as TaskSnapshot) : null
}

// The scheduled delivery window the backend stamps onto a windowed publish
// (and its sibling removal task) — {start_at, end_at} ISO strings, either side
// nullable. Drives the "Scheduled …" status chip + the time-frame line.
type TaskWindow = { start_at: string | null; end_at: string | null }
const taskWindow = (task: Task): TaskWindow | null => {
  const w = (task.meta as { window?: unknown } | null | undefined)?.window
  if (!w || typeof w !== 'object') return null
  const { start_at = null, end_at = null } = w as TaskWindow
  return start_at || end_at ? { start_at, end_at } : null
}

// The moment this task "activates": a removal settles at end_at, everything
// else at start_at. Used to tell a still-pending scheduled task (window not
// open yet) from one that's genuinely stalled waiting on a screen.
const activationAt = (task: Task, win: TaskWindow): string | null =>
  task.type === 'content_expire' ? win.end_at : win.start_at

const isScheduledPending = (task: Task): boolean => {
  if (task.status !== 'pending') return false
  const win = taskWindow(task)
  if (!win) return false
  const at = activationAt(task, win)
  return !!at && new Date(at).getTime() > Date.now()
}

// Compact "contents" line for the frozen snapshot: the first few item names +
// how many more, falling back to a per-kind descriptor (layout zones / menu
// products) when the content has no named items. Null when there's nothing
// worth showing (single media/audio files — the row title already names them).
function snapshotSummary(snap: TaskSnapshot): string | null {
  const names = snap.item_names?.filter(Boolean) ?? []
  if (names.length) {
    const shown = names.slice(0, 3).join(', ')
    const more = (snap.item_count ?? names.length) - Math.min(3, names.length)
    return more > 0 ? `${shown} +${more}` : shown
  }
  return null
}

// Returns translation keys (not text) so the caller owns the `t()` call —
// keeps this helper free of react-i18next's TFunction typing.
// Canonical status buckets shown to operators: Running / Pending / Completed /
// Failed. Publish tasks carry the full lifecycle; screen-less activity tasks
// only ever land Completed (or Failed).
function statusChip(task: Task): { labelKey: string; fallback: string; cls: string } {
  if (task.status === 'failed')
    return { labelKey: 'tasks.status.failed', fallback: 'Failed', cls: 'bg-red-500/15 text-red-500' }
  if (task.status === 'done')
    return { labelKey: 'tasks.status.completed', fallback: 'Completed', cls: 'bg-emerald-500/15 text-emerald-500' }
  if (PUBLISH_TYPES.has(task.type)) {
    if (task.status === 'pending') {
      // A window that hasn't opened yet reads "Scheduled" (queued for its
      // moment), distinct from a stalled publish waiting on an offline screen.
      if (isScheduledPending(task))
        return { labelKey: 'tasks.status.scheduled', fallback: 'Scheduled', cls: 'bg-violet-500/15 text-violet-500' }
      return { labelKey: 'tasks.status.pending', fallback: 'Pending', cls: 'bg-amber-500/15 text-amber-600' }
    }
    return { labelKey: 'tasks.status.running', fallback: 'Running', cls: 'bg-blue-500/15 text-blue-500' }
  }
  // upload / archive / restore / rename — recorded complete on creation.
  return { labelKey: 'tasks.status.running', fallback: 'Running', cls: 'bg-blue-500/15 text-blue-500' }
}

// One row inside the "Running now" panel — a live progress bar.
export function RunningRow({ task }: { task: AnyTask }) {
  const Icon = activityMetaFor(task.type).icon
  let right: string
  let value: number
  if (isUploadTask(task)) {
    right = `${task.progress}%`
    value = task.progress
  } else {
    right = `${appliedCount(task)}/${task.screens.length}`
    value = task.progress
  }
  return (
    <div className="flex items-center gap-3">
      <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm truncate">{task.target_label}</span>
          <span className="text-xs tabular-nums text-muted-foreground shrink-0">{right}</span>
        </div>
        <Progress value={value} className="h-1.5 mt-1.5" />
      </div>
    </div>
  )
}

// One card in the full feed.
// Localized short date+time for a schedule boundary (e.g. "13 Jul, 18:00").
function formatWhen(iso: string, lang: string): string {
  return new Date(iso).toLocaleString(lang === 'ar' ? 'ar-SA' : 'en-GB', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  })
}

export function TaskRow({ task }: { task: Task }) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const meta = activityMetaFor(task.type)
  const Icon = meta.icon
  // Unified headline — "<Subject> <Verb-ed> (<name>)", e.g. "Playlist
  // Published (Morning Promo)" — so the row reads the same shape as every
  // other activity type across both feeds.
  const title = activityTitle(t, task.type, task.target_label)
  const publish = isPublish(task)
  const total = task.screens.length
  const applied = appliedCount(task)
  const chip = statusChip(task)
  // Running = actively delivering (not queued/finished). Drives the progress
  // bar: shown while running, greyed while pending, hidden once done/failed.
  const isRunning = task.status !== 'pending' && task.status !== 'done' && task.status !== 'failed'
  const summary = publish ? screenSummary(task) : null
  const route = taskContentRoute(task)
  const snap = publish ? taskSnapshot(task) : null
  const contents = snap ? snapshotSummary(snap) : null
  // Grouped publishes (playlist / layout / audio playlist) reveal their frozen
  // item list when expanded.
  const grouped = GROUPED_TYPES.has(task.type)
  const groupItems = grouped ? (snap?.item_names ?? []) : []
  const groupMore = grouped && snap?.item_count ? snap.item_count - groupItems.length : 0

  // Scheduled time-frame line — the delivery window an operator picked. A
  // removal task leads with its end (that's when it fires); a windowed publish
  // shows whichever bounds it has.
  const win = publish ? taskWindow(task) : null
  const scheduleLine = ((): string | null => {
    if (!win) return null
    const start = win.start_at ? formatWhen(win.start_at, i18n.language) : null
    const end = win.end_at ? formatWhen(win.end_at, i18n.language) : null
    if (task.type === 'content_expire')
      return end ? t('tasks.endsAt', { defaultValue: 'Ends {{time}}', time: end }) : null
    if (start && end) return t('tasks.scheduledWindow', { defaultValue: 'Scheduled {{start}} → {{end}}', start, end })
    if (end) return t('tasks.endsAt', { defaultValue: 'Ends {{time}}', time: end })
    if (start) return t('tasks.startsAt', { defaultValue: 'Starts {{time}}', time: start })
    return null
  })()

  return (
    // Pending activities read greyed until they start delivering.
    <div className={cn('rounded-xl card-inner p-3', task.status === 'pending' && 'opacity-70')}>
      <div className="flex items-center gap-3">
        <span className="w-8 h-8 rounded-lg bg-card flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4 text-muted-foreground" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {/* The unified headline links to the thing that was published. */}
            {route ? (
              <button
                type="button"
                onClick={() => navigate(route)}
                className="text-sm font-medium truncate text-start hover:text-primary hover:underline transition-colors"
              >
                {title}
              </button>
            ) : (
              <span className="text-sm font-medium truncate">{title}</span>
            )}
            <span className={cn('text-[11px] px-2 py-0.5 rounded-full font-semibold shrink-0', chip.cls)}>{t(chip.labelKey, chip.fallback)}</span>
          </div>
          {/* Second line answers "by whom, to whom, when" — the title above
              already says what happened, so this doesn't repeat it. */}
          <p
            className="text-[11px] text-muted-foreground mt-0.5 truncate"
            title={new Date(task.created_at).toLocaleString()}
          >
            {task.actor_name && (
              <>{t('tasks.byActor', { defaultValue: 'by {{name}}', name: task.actor_name })}</>
            )}
            {summary && (
              <>{task.actor_name && ' · '}{t('tasks.toScreens', 'to')} <span className="text-foreground/80 font-medium">{summary}</span></>
            )}
            {(task.actor_name || summary) && ' · '}{formatRelativeSyncTime(task.created_at)}
          </p>
          {/* Frozen contents of the publish — stays accurate even after the
              live content is edited or deleted (its {kind,id} link may 404). */}
          {contents && (
            <p className="text-[11px] text-muted-foreground/80 mt-0.5 truncate">
              {t('tasks.snapshotContents', { defaultValue: 'Included: {{items}}', items: contents })}
            </p>
          )}
          {/* Scheduled time frame — the delivery window (start/end) an operator
              picked, so a pending row says *when* it will run, not just that
              it's waiting. */}
          {scheduleLine && (
            <p className="text-[11px] text-violet-500 mt-0.5 flex items-center gap-1 truncate">
              <CalendarClock className="w-3 h-3 shrink-0" />
              <span className="truncate">{scheduleLine}</span>
            </p>
          )}
        </div>
        {publish && total > 0 && (
          <button
            type="button"
            onClick={() => setOpen(o => !o)}
            className="w-7 h-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted shrink-0"
            aria-label={t('tasks.toggleScreens', 'Toggle screens')}
          >
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        )}
      </div>

      {publish && total > 0 && (
        <div className="mt-2 ps-11">
          <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1">
            <span>
              {t('tasks.screensApplied', { defaultValue: '{{applied}} of {{total}} screens applied', applied, total })}
            </span>
            {/* Percentage only reads while the delivery is actively running. */}
            {isRunning && <span className="tabular-nums">{task.progress}%</span>}
          </div>
          {/* Progress bar: a live bar while running, a greyed (empty) track
              while pending, and nothing once the task has succeeded or failed —
              the status chip + per-screen ledger carry the outcome from there. */}
          {isRunning
            ? <Progress value={task.progress} className="h-1.5" />
            : task.status === 'pending'
              ? <div className="h-1.5 rounded-full bg-muted/60" aria-hidden />
              : null}
          {/* Failed = the delivery window (24h) lapsed with screens still
              waiting — say so instead of leaving a bare red chip. */}
          {task.status === 'failed' && applied < total && (
            <p className="text-[11px] text-destructive mt-1.5">
              {t('tasks.failedReason', {
                defaultValue: '{{count}} screens never received the content — they may be offline or unlinked.',
                count: total - applied,
              })}
            </p>
          )}
          {open && grouped && groupItems.length > 0 && (
            // The frozen "what was inside" list for grouped content (playlist
            // items / layout zones / audio tracks), captured at publish time.
            <div className="mt-2 rounded-md border border-border/60 bg-muted/20 p-2">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                {t('tasks.contents', 'Contents')}
              </p>
              <ul className="space-y-0.5">
                {groupItems.map((name, i) => (
                  <li key={i} className="text-[11px] text-foreground/80 truncate">{name}</li>
                ))}
                {groupMore > 0 && (
                  <li className="text-[11px] text-muted-foreground">
                    {t('tasks.moreItems', { defaultValue: '+{{count}} more', count: groupMore })}
                  </li>
                )}
              </ul>
            </div>
          )}
          {open && (
            // Per-screen delivery ledger: who got it (and when the device
            // pulled it) vs who is still waiting (and since when). Delivered
            // rows sort first so the stragglers cluster at the bottom.
            <ul className="mt-2 space-y-1">
              {[...task.screens]
                .sort((a, b) => (a.status === b.status ? 0 : a.status === 'applied' ? -1 : 1))
                .map(sc => {
                  const delivered = sc.status === 'applied'
                  const inner = (
                    <>
                      {delivered
                        ? <Check className="w-3 h-3 text-emerald-500 shrink-0" />
                        : <Clock className="w-3 h-3 text-amber-600 shrink-0" />}
                      <span className="truncate flex-1 font-medium">{screenName(sc)}</span>
                      <span className={cn('shrink-0 font-medium', delivered ? 'text-emerald-500' : 'text-amber-600')}>
                        {delivered
                          ? t('tasks.screenDelivered', 'Delivered')
                          : t('tasks.screenWaiting', 'Waiting')}
                      </span>
                      {/* Delivered → when the device pulled it; Waiting →
                          how long ago it was published to this screen. */}
                      <span className="text-muted-foreground shrink-0 tabular-nums">
                        {formatRelativeSyncTime(delivered && sc.applied_at ? sc.applied_at : sc.published_at)}
                      </span>
                    </>
                  )
                  const rowCls = 'flex items-center gap-2 text-[11px] rounded-md border border-border/60 bg-muted/30 px-2 py-1.5'
                  return (
                    <li key={sc.id}>
                      {/* Rows deep-link to the screen page's Activity Log
                          tab when the row carries the screen's public id
                          (newer tasks) — a Waiting screen's log shows WHY
                          (offline since…, content retries). */}
                      {sc.screen_public_id ? (
                        <button
                          type="button"
                          onClick={() => navigate(`/screens/${sc.screen_public_id}?tab=activity`)}
                          className={cn(rowCls, 'w-full text-start hover:bg-muted/60 transition-colors')}
                        >
                          {inner}
                        </button>
                      ) : (
                        <div className={rowCls}>{inner}</div>
                      )}
                    </li>
                  )
                })}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
